<script setup>
    // Hilfsfunktion für Highlighting
    function escapeHtml(str) {
        return String(str ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    }
    function highlightMatch(text, search) {
        const safe = escapeHtml(text);
        if (!search) return safe;
        const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return safe.replace(regex, '<mark class="search-highlight">$1</mark>');
    }
    // Seite zum Verschieben vergangener Wiedervorlagen
    import { ref, computed, onMounted } from 'vue'
    import { useRouter } from 'vue-router'

    import Reminder from '@/components/Reminder.vue'
    import userAvatar from '@/components/userAvatar.vue'
    import Spinner from '@/components/Spinner.vue'
    // import DatePickerNextWithEvents from '@/components/ui/DatePickerNextWithEvents.vue'

    import { useUserStore } from '@/stores/user'
    const userStore = useUserStore()
    const router = useRouter()

    import { useBusinessPartnersStore } from '@/stores/businessPartners'
    const businessPartnerStore = useBusinessPartnersStore()

    const today = new Date().toISOString().slice(0, 10)

    // Events aus Reminder: VorOrtTermine, nur zukünftige Termine
    const salesManagerEvents = computed(() =>
      businessPartnerStore.reminders['VorOrtTermine']
        .filter(ev => {
          const date = ev.businessPartner.salesPitch.dateFollowUp?.slice(0, 10)
          return date && date >= today
        })
        .map(ev => ({
          date: ev.businessPartner.salesPitch.dateFollowUp.slice(0, 10),
          type: ev.businessPartner.salesPitch.comment || 'Vor Ort Termin',
          name: ev.businessPartner.name
        }))
    )

    const loading = ref(false)
    const isSaving = ref(false)

    import { useBusinessPartnersCountStore } from '@/stores/businessPartnersCount'
    const businessPartnersCountStore = useBusinessPartnersCountStore();



const reschedule = ref([])
const searchTerm = ref('');
const sortKey = ref('index');
const sortDir = ref(1); // 1=asc, -1=desc

onMounted(() => {
    const businessPartnerList = businessPartnerStore.businessPartnerListBySignature(`${userStore.currentSelection?.salesAgent?.id}:${userStore.currentSelection.campaign.id}#ToDo:Wiedervorlagen`)
    reschedule.value = businessPartnerList.map((businessPartner) => ({
        id: businessPartner.salesPitch.id,
        businessPartner: {
            id: businessPartner.id,
            gpNo: businessPartner.gpNo || (businessPartner.meta && businessPartner.meta.gpNo) || '',
            name: businessPartner.name,
            notes: businessPartner.notes,
            address: {
                city: businessPartner.addressList[0].city.name,
                houseNumber: businessPartner.addressList[0].houseNumber,
                name: businessPartner.addressList[0].name,
            },
            salesPitch: {
                count: businessPartner.salesPitch.count,
                comment: businessPartner.salesPitch.comment,
            },
            salesAgent: businessPartner.salesAgent,
            salesAgentId: businessPartner.idSalesAgent,
        },
        rescheduleDate: businessPartner.salesPitch?.rescheduleDate || '',
        rescheduleTime: null,
        selected: false,
        unscheduled: true,
        isValid: false,
    }))
})

const filteredReschedule = computed(() => {
    const term = searchTerm.value.trim().toLowerCase();
    // Show all unscheduled To-Dos, regardless of date
    return reschedule.value.filter(item =>
        item.unscheduled && (
            !term ||
            item.businessPartner.name.toLowerCase().includes(term) ||
            item.businessPartner.gpNo.toLowerCase().includes(term) ||
            (item.businessPartner.salesPitch.comment || '').toLowerCase().includes(term)
        )
    );
});

function sortBy(key) {
    if (sortKey.value === key) {
        sortDir.value = -sortDir.value;
    } else {
        sortKey.value = key;
        sortDir.value = 1;
    }
}

const sortedReschedule = computed(() => {
    const arr = filteredReschedule.value.slice();
    switch (sortKey.value) {
        case 'name':
            arr.sort((a, b) => a.businessPartner.name.localeCompare(b.businessPartner.name) * sortDir.value); break;
        case 'gpNo':
            arr.sort((a, b) => a.businessPartner.gpNo.localeCompare(b.businessPartner.gpNo) * sortDir.value); break;
        case 'count':
            arr.sort((a, b) => (a.businessPartner.salesPitch.count - b.businessPartner.salesPitch.count) * sortDir.value); break;
        default:
            // index: keep original order
            break;
    }
    return arr;
});

const searchTermLower = computed(() => searchTerm.value.trim().toLowerCase());

const unscheduled = computed(() => (reschedule.value) ? reschedule.value.filter(item => item.unscheduled).length : 0)


const handleReschedule = async (id) => {
    isSaving.value = true
    try {
        const entry = reschedule.value.find((item) => item.businessPartner.id == id)
        const rescheduleSuccess = await businessPartnerStore.rescheduleSalesPitch(entry)
        await businessPartnerStore.getByFilter(businessPartnerStore.namedFilter('ToDo:Wiedervorlagen'))
        if (rescheduleSuccess) entry.unscheduled = false
        await userStore.refreshFollowUps()
        if (reschedule.value.filter(item => item.unscheduled).length === 0) router.push({ name: 'Dashboard', hash: '#potential-bk' })
    } finally {
        isSaving.value = false
    }
}


const handleSetDate = async (id) => {
    const entry = reschedule.value.find((item) => item.businessPartner.id == id)
    // console.log('set date:', entry)
    entry.isValid = (entry.rescheduleDate) ? true : false
}

const handleNameClick = async (gpNo) => {
    loading.value = true
    try {
        await router.push({ name: 'BusinessPartner', query: { searchMode: 'GPNo', search: gpNo } })
    } finally {
        loading.value = false
    }
}

</script>

<template>
    <div class="reminder-wrapper singlepage-wrapper max-width">
        <Spinner v-if="loading" text="wird geöffnet..." />
        <Spinner v-if="isSaving" text="wird gespeichert..." />
        <main id="container-main">
            <!-- Mobile: Kacheln wie gehabt -->
            <div class="mobile-only">
                <div class="mobile-card-list">
                    <div class="mobile-card" v-for="(entry, index) in filteredReschedule || []" :key="entry.businessPartner.id">
                        <div class="mobile-card-header">
                            <span class="mobile-card-index">{{ index + 1 }}</span>
                            <span class="bp-pill gp">GP: <strong>{{ entry.businessPartner.gpNo }}</strong></span>
                            <span class="mobile-card-count">#{{ entry.businessPartner.salesPitch.count }}</span>
                        </div>
                        <div class="mobile-card-title">
                            <a href="#" @click.prevent="handleNameClick(entry.businessPartner.gpNo)">{{ entry.businessPartner.name }}</a>
                        </div>
                        <div class="mobile-card-comment">
                            <textarea readonly :value="entry.businessPartner.salesPitch.comment || 'Kein Kommentar vorhanden.'"></textarea>
                        </div>
                        <div class="mobile-card-inputs">
                            <input class="form-child" @change="handleSetDate(entry.businessPartner.id)" type="date" v-model="entry.rescheduleDate">
                            <input class="form-child" @change="handleSetDate(entry.businessPartner.id)" type="time" list="appt-time" v-model="entry.rescheduleTime"/>
                        </div>
                        <button class="btn btn--pill btn--primary mobile-card-btn" @click="handleReschedule(entry.businessPartner.id)" :disabled="userStore.preventSave || !entry.isValid || userStore.currentSelection.scope!=='salesAgent' || isSaving">Speichern</button>
                    </div>
                </div>
            </div>
            <!-- Desktop: Listenansicht, volle Breite -->
                        <div class="desktop-only">
                            <div class="headline-toolbar">
                                <div class="headline-wrapper">
                                    <h2>Wiedervorlagen To-Do</h2>
                                </div>
                            </div>
                            <div class="table-toolbar">
                                <router-link to="/dashboard" class="back-btn">← Zurück zum Dashboard</router-link>
                                <div class="table-search-wrapper">
                                    <input v-model="searchTerm" class="table-search" placeholder="Suchen..." />
                                    <span class="table-count">{{ filteredReschedule.length }} Einträge</span>
                                </div>
                            </div>
                            <table class="followup-table modern">
                                <thead>
                                    <tr>
                                        <th @click="sortBy('index')" :class="{sorted: sortKey==='index', sortable: true}">
                                            #
                                            <span class="sort-icon" :class="{active: sortKey==='index', desc: sortKey==='index' && sortDir===-1}">
                                                <svg width="13" height="13" viewBox="0 0 13 13"><path d="M3.5 8l3-3 3 3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                            </span>
                                        </th>
                                        <th @click="sortBy('name')" :class="{sorted: sortKey==='name', sortable: true}">
                                            Name
                                            <span class="sort-icon" :class="{active: sortKey==='name', desc: sortKey==='name' && sortDir===-1}">
                                                <svg width="13" height="13" viewBox="0 0 13 13"><path d="M3.5 8l3-3 3 3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                            </span>
                                        </th>
                                        <th @click="sortBy('gpNo')" :class="{sorted: sortKey==='gpNo', sortable: true}">
                                            GPNo
                                            <span class="sort-icon" :class="{active: sortKey==='gpNo', desc: sortKey==='gpNo' && sortDir===-1}">
                                                <svg width="13" height="13" viewBox="0 0 13 13"><path d="M3.5 8l3-3 3 3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                            </span>
                                        </th>
                                        <th>Kommentar</th>
                                        <th @click="sortBy('count')" :class="{sorted: sortKey==='count', sortable: true}">
                                            Pitch #
                                            <span class="sort-icon" :class="{active: sortKey==='count', desc: sortKey==='count' && sortDir===-1}">
                                                <svg width="13" height="13" viewBox="0 0 13 13"><path d="M3.5 8l3-3 3 3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                            </span>
                                        </th>
                                        <th>Datum</th>
                                        <th>Uhrzeit</th>
                                        <th>Aktion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(entry, index) in sortedReschedule" :key="entry.businessPartner.id">
                                        <td>{{ index + 1 }}</td>
                                        <td>
                                            <a href="#" class="customer-link" @click.prevent="handleNameClick(entry.businessPartner.gpNo)">
                                                <span class="avatar-cell"><userAvatar :salesAgentId="entry.businessPartner.salesAgentId" v-if="userStore.currentSelection.scope!=='salesAgent'" :showSalesAgent="true" :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback" /></span>
                                                <span class="customer-name" v-html="highlightMatch(entry.businessPartner.name, searchTermLower)"></span>
                                            </a>
                                        </td>
                                        <td>
                                            <span class="bp-pill gp"><span v-html="highlightMatch(entry.businessPartner.gpNo, searchTermLower)"></span></span>
                                        </td>
                                        <td class="comment-cell">
                                            <textarea readonly rows="2" :value="entry.businessPartner.salesPitch.comment || 'Kein Kommentar vorhanden.'"></textarea>
                                            <span v-if="searchTermLower && (entry.businessPartner.salesPitch.comment || '').toLowerCase().includes(searchTermLower)" class="search-highlight-comment" v-html="highlightMatch(entry.businessPartner.salesPitch.comment || 'Kein Kommentar vorhanden.', searchTermLower)"></span>
                                        </td>
                                        <td>#{{ entry.businessPartner.salesPitch.count }}</td>
                                        <td>
                                            <input class="form-child" @change="handleSetDate(entry.businessPartner.id)" type="date" v-model="entry.rescheduleDate">
                                        </td>
                                        <td>
                                            <input class="form-child" @change="handleSetDate(entry.businessPartner.id)" type="time" list="appt-time" v-model="entry.rescheduleTime"/>
                                        </td>
                                        <td class="action-cell">
                                            <button class="btn primary" @click="handleReschedule(entry.businessPartner.id)" :disabled="userStore.preventSave || !entry.isValid || userStore.currentSelection.scope!=='salesAgent' || isSaving">
                                                Speichern
                                            </button>
                                            <button class="btn subtle" @click="handleNameClick(entry.businessPartner.gpNo)">
                                                Details
                                            </button>
                                        </td>
                                    </tr>
                                    <!-- <tr v-if="sortedReschedule.length === 0">
                                        <td colspan="8" class="empty-row">Keine Einträge gefunden.</td>
                                    </tr> -->
                                </tbody>
                            </table>
                        </div>
        </main>
        <reminder></reminder>
    </div>
</template>

<style scoped>
th.sortable {
    cursor: pointer;
    user-select: none;
    position: relative;
}
.sort-icon {
    display: inline-block;
    vertical-align: middle;
    margin-left: 0.32em;
    color: #bfc6d1;
    transition: color 0.18s, transform 0.18s;
    line-height: 1;
    transform: rotate(0deg);
}
.sort-icon svg {
    display: block;
}
.sort-icon.active {
    color: #4bb6a7;
}
.sort-icon.desc {
    transform: rotate(180deg);
}
mark.search-highlight {
    background: #ffe066;
    color: #222;
    padding: 0 2px;
    border-radius: 3px;
}
.search-highlight-comment {
    display: block;
    margin-top: 2px;
    font-size: 0.97em;
    background: #fffbe6;
    border-radius: 3px;
    padding: 2px 4px;
    color: #b8860b;
    font-weight: 500;
}
.back-btn { font-size:.6rem; font-weight:600; text-decoration:none; background:#fff; border:1px solid #e2e8f0; padding:.4rem .65rem; border-radius:8px; color:#334155; display:inline-flex; gap:.35rem; align-items:center; letter-spacing:.05em; }
.back-btn:hover { background:#f1f5f9; }
.theme-dark .back-btn { background:#1e293b; border-color:#334155; color:#e2e8f0; }
.theme-dark .back-btn:hover { background:#334155; }
.headline-toolbar {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: 1.5rem 0 0.5rem 0;
}
.headline-wrapper {
    margin-bottom: 0.2rem;
}
.spinner {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(255,255,255,0.7);
    z-index: 9999;
    font-size: 1.5rem;
}
.mobile-only { display: block; }
.desktop-only { display: none; }
@media (min-width: 1024px) {
    .mobile-only { display: none !important; }
    .desktop-only { display: block; }
    .table-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1.2rem;
        gap: 1.5rem;
    }
    .table-search-wrapper {
        display: flex;
        align-items: center;
        gap: 1.1rem;
        background: #f7fafd;
        border-radius: 8px;
        padding: 0.3rem 1.1rem 0.3rem 0.7rem;
        box-shadow: 0 1px 4px 0 rgba(40,129,126,0.04);
    }
    .table-search {
        padding: 0.5rem 1rem;
        border-radius: 6px;
        border: 1px solid #d0d0d0;
        font-size: 1rem;
        width: 220px;
        background: #fafbfc;
        transition: border 0.2s;
    }
    .table-search:focus {
        border: 1.5px solid #4bb6a7;
        outline: none;
    }
    .table-count {
        color: #888;
        font-size: 1rem;
        margin-left: 0.5rem;
    }
    .followup-table.modern {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        margin: 0 0 2rem 0;
        background: #fff;
        box-shadow: 0 2px 16px 0 rgba(0,0,0,0.07);
        border-radius: 12px;
        overflow: hidden;
        font-size: 1.04rem;
        table-layout: auto;
    }
    .followup-table.modern thead th {
        position: sticky;
        top: 0;
        background: #f7fafd;
        font-weight: 700;
        font-size: 1.05rem;
        padding: 0.9rem 0.7rem;
        border-bottom: 2px solid #e0e7ef;
        cursor: pointer;
        user-select: none;
        transition: background 0.15s;
    }
    .followup-table.modern thead th.sorted {
        background: rgba(75,182,167,0.075);
        color: #4bb6a7;
    }
    .followup-table.modern tbody tr {
        transition: background 0.15s;
    }
    .followup-table.modern tbody tr:nth-child(even) {
        background: #f8fafc;
    }
    .followup-table.modern tbody tr:hover {
        background: rgba(75,182,167,0.075);
    }
    .followup-table.modern td, .followup-table.modern th {
        padding: 0.7rem 0.7rem;
        border-bottom: 1px solid #e0e7ef;
        vertical-align: middle;
    }
    .followup-table.modern tr:last-child td {
        border-bottom: none;
    }
    .followup-table.modern .avatar-cell {
        display: inline-block;
        margin-right: 0.4rem;
        vertical-align: middle;
    }
    .followup-table.modern .customer-link {
        color: #28817e;
        text-decoration: none;
        font-weight: 500;
        transition: color 0.15s, background 0.15s, box-shadow 0.15s;
        border-radius: 7px;
        padding: 0.04rem 0.18rem;
        display: inline-flex;
        align-items: center;
        gap: 0.22rem;
        box-shadow: 0 1px 4px 0 rgba(75,182,167,0.04);
        background: transparent;
    }
    .followup-table.modern .customer-link:hover,
    .followup-table.modern .customer-link:focus {
        /* color: #28817e; */
        /* background: #e6f4f3; */
        text-decoration: underline;
        outline: none;
        /* box-shadow: 0 1px 6px 0 rgba(75,182,167,0.07); */
    }
    .followup-table.modern .customer-link .customer-name {
        font-weight: 600;
        font-size: 0.98rem;
        letter-spacing: 0.01em;
        padding-right: 0.05em;
    }
    .bp-pill { display:inline-flex; align-items:center; gap:4px; padding:3px 10px 4px; border-radius:999px; font-size:11px; font-weight:600; line-height:1.1; letter-spacing:.3px; background:var(--pill-bg,#eef1f4); color:var(--pill-fg,#1f2730); border:1px solid var(--pill-border,#d7dde2); transition:background .2s ease, color .2s ease, border-color .2s ease; }
    .bp-pill strong { font-weight:700; }
    .bp-pill.gp { --pill-bg:#f5f7f9; --pill-border:#e3e8ec; --pill-fg:#1f2730; }
    .followup-table.modern td, .followup-table.modern th {
        padding: 0.55rem 0.6rem;
        font-size: 0.97rem;
        border-bottom: 1px solid #e0e7ef;
        vertical-align: middle;
    }
    .followup-table.modern th {
        font-size: 1.01rem;
        font-weight: 600;
        letter-spacing: 0.01em;
    }
    /* Button styles now use global .btn classes from globals.scss */
    .followup-table.modern .comment-cell textarea {
        font-size: 0.96rem;
        border-radius: 7px;
        border: 1px solid #e0e7ef;
        background: #f6faf9;
        color: #222;
        padding: 0.32rem 0.6rem;
        min-width: 120px;
        max-width: 260px;
        min-height: 2em;
        max-height: 5em;
        box-shadow: 0 1px 4px 0 rgba(40,129,126,0.04);
    }
    .followup-table.modern .action-cell {
        gap: 0.25rem;
    }
    .followup-table.modern {
        border-radius: 13px;
        box-shadow: 0 2px 16px 0 rgba(40,129,126,0.07);
    }
    .followup-table.modern .comment-cell textarea {
        width: 100%;
        min-width: 180px;
        max-width: 340px;
        min-height: 2.2em;
        max-height: 5.5em;
        resize: vertical;
        border-radius: 6px;
        border: 1px solid #d0d0d0;
        background: #f7fafd;
        font-size: 1rem;
        padding: 0.4rem 0.7rem;
        color: #222;
        outline: none;
        transition: border 0.2s;
    }
    .followup-table.modern .comment-cell textarea:focus {
        border: 1.5px solid #4bb6a7;
    }
    .followup-table.modern .action-cell {
        display: flex;
        gap: 0.5rem;
        align-items: center;
    }
    /* Removed local .ci-btn styles in favor of global .btn system */
    .followup-table.modern .empty-row {
        text-align: center;
        color: #888;
        font-style: italic;
        background: #f8fafc;
    }
}
/* Modernes Card-Design für mobile Kacheln */
.mobile-card-list {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    padding: 0.5rem 0.2rem 1.5rem 0.2rem;
}
.mobile-card {
    background: #fff;
    border-radius: 18px;
    box-shadow: 0 2px 12px 0 rgba(40,129,126,0.10);
    padding: 1.1rem 1.2rem 1.2rem 1.2rem;
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    border: 1px solid #e3e8ec;
    position: relative;
}
.mobile-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.7rem;
    margin-bottom: 0.2rem;
    position: relative;
}
.mobile-card-index {
    background: #e3e8ec;
    color: #4bb6a7;
    font-weight: 600;
    border-radius: 50%;
    width: 1.7em;
    height: 1.7em;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.98em;
    box-shadow: none;
    border: 1px solid #e3e8ec;
    opacity: 0.7;
}
.mobile-card-count {
    font-size: 0.98em;
    color: #b2dad5;
    font-weight: 600;
    margin-left: auto;
}
.mobile-card-title {
    font-size: 1.13em;
    font-weight: 600;
    color: #28817e;
    margin-bottom: 0.1em;
}
.mobile-card-title a {
    color: inherit;
    text-decoration: none;
}
.mobile-card-title a:hover {
    text-decoration: underline;
}
.mobile-card-comment textarea {
    width: 100%;
    min-height: 2.2em;
    max-height: 5.5em;
    resize: vertical;
    border-radius: 8px;
    border: 1px solid #d0d0d0;
    background: #f7fafd;
    font-size: 1em;
    padding: 0.4rem 0.7rem;
    color: #222;
    outline: none;
    transition: border 0.2s;
}
.mobile-card-inputs {
    display: flex;
    gap: 0.7rem;
    margin-bottom: 0.2em;
}
.mobile-card-inputs input[type="date"],
.mobile-card-inputs input[type="time"] {
    flex: 1 1 0;
    border-radius: 7px;
    border: 1px solid #d0d0d0;
    background: #f7fafd;
    font-size: 1em;
    padding: 0.4rem 0.7rem;
    color: #222;
    outline: none;
    transition: border 0.2s;
}
.mobile-card-btn {
    margin-top: 0.2em;
    align-self: flex-end;
    min-width: 110px;
}
</style>